import React, { useEffect, useRef, useState } from "react";
import { msalInstance } from "../../../msalInstance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../shared/axiosInstance";
import { API } from "../../../constants";

const AuthCallback: React.FC = () => {
  const [status, setStatus] = useState("Processing login...");
  const navigate = useNavigate();

  const hasCalledBackend = useRef(false);

  useEffect(() => {
    const handleRedirect = async () => {
      if (hasCalledBackend.current) {
        return;
      }

      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          const claims = response.account?.idTokenClaims;
          const groups = claims?.groups || [];

          if (!response.idToken) {
            toast.error("Authentication failed! No ID token received.");
            msalInstance.clearCache();
            sessionStorage.clear();
            navigate("/");
            return;
          }

          if (
            !groups ||
            !Array.isArray(groups) ||
            !groups.some(
              (g: string) =>
                g === process.env.REACT_APP_ENTRA_SUP_ADMIN_GROUP_ID ||
                g === process.env.REACT_APP_ENTRA_SHIP_HR_GROUP_ID
            )
          ) {
            toast.error(
              "You are not authorized to access this application. Please contact support."
            );
            msalInstance.clearCache();
            sessionStorage.clear();
            navigate("/");
            return;
          }

          setStatus("Verifying credentials...");

          try {
            hasCalledBackend.current = true;
            const entraCallbackResponse = await axiosInstance.post(
              `/${API.AUTH.ENTRA_CALLBACK}`,
              {
                idToken: response.idToken,
              }
            );

            if (entraCallbackResponse.data.status === "SUCCESS") {
              if (entraCallbackResponse.data.csrfToken) {
                sessionStorage.setItem(
                  "csrfToken",
                  entraCallbackResponse.data.csrfToken
                );
              }

              msalInstance.setActiveAccount(response.account);

              sessionStorage.setItem("entraLogin", "true");
              sessionStorage.setItem("token", response.idToken);

              setStatus("Login successful");

              toast.success("Login successful!");

              navigate("/dashboard");
            } else {
              throw new Error("Backend authentication failed");
            }
          } catch (backendError: any) {
            console.error("authentication error:", backendError);

            hasCalledBackend.current = false;

            const errorMessage =
              backendError?.response?.data?.message ||
              "Failed to complete authentication. Please try again.";

            toast.error(errorMessage);

            msalInstance.clearCache();
            sessionStorage.clear();
            navigate("/");
            return;
          }
        } else {
          const account = msalInstance.getActiveAccount();
          const isEntraLogin = sessionStorage.getItem("entraLogin") === "true";

          if (account && isEntraLogin) {
            setStatus("Already logged in");
            navigate("/dashboard");
          } else {
            setStatus("No active session");
            navigate("/");
          }
        }
      } catch (err: any) {
        console.error("Authentication error:", err);
        setStatus("Login failed");

        toast.error(
          "An error occurred during authentication. Please try again."
        );

        msalInstance.clearCache();
        sessionStorage.clear();
        hasCalledBackend.current = false;
        navigate("/");
      }
    };

    handleRedirect();
  }, [navigate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "2rem",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h1>{status}</h1>
        <p>Please wait while we complete your authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
